import { useLocalStorageState } from 'ahooks';
import { updateProfile, User } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { EmailDigestSettings } from 'src/components/EmailDigestSettings';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { useFirebaseAuth } from 'src/contexts/AuthContext/FirebaseAuthContext';
import { useNotification } from 'src/contexts/NotificationContext';

export const Profile = () => {
  const notification = useNotification();
  const auth = useFirebaseAuth();
  const currentUser = auth?.user as User | null;

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [, setFireBaseDisplayName] = useLocalStorageState<string | null>(
    '@control-plane/fireBaseDisplayName',
    {
      defaultValue: '',
      listenStorageChange: true,
    },
  );

  useEffect(() => {
    if (currentUser) {
      const displayName = currentUser.displayName || '';
      const [firstName, ...lastName] = displayName.split(' ');
      setFirstName(firstName || '');
      setLastName(lastName.join(' ') || '');
    }
  }, [currentUser]);

  const handleUpdateProfile = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      await updateProfile(currentUser, {
        displayName: `${firstName.trim()} ${lastName.trim()}`,
      });

      setFireBaseDisplayName(`${firstName} ${lastName}`);
      notification.success('Profile updated successfully');
    } catch (err) {
      notification.error('Failed to update profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1">
      <Heading title="Profile" subtitle="Update your profile information" />

      <ContentWrapper>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 place-items-center">
          <div className="w-full max-w-96">
            {error && <div className="text-error mb-4">{error}</div>}

            <div className="mb-6">
              <Input
                label="First Name"
                type="text"
                id="first-name"
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value.slice(0, 40))}
                disabled={loading}
                required
              />
            </div>

            <div className="mb-6">
              <Input
                label="Last Name"
                type="text"
                id="last-name"
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value.slice(0, 40))}
                disabled={loading}
                required
              />
            </div>

            <div className="flex justify-end italic text-sm mb-4">
              * Required fields
            </div>

            <Button
              className="w-full"
              onClick={handleUpdateProfile}
              disabled={loading || !firstName || !lastName}
              variant="contained"
              color="primary"
              size="medium"
            >
              {loading ? 'Saving...' : 'Update Profile'}
            </Button>
          </div>

          <div className="w-full max-w-96">
            <EmailDigestSettings disabled={loading} />
          </div>
        </div>
      </ContentWrapper>
    </div>
  );
};
